import React, { useEffect, useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { SIPProvider, useSIPProvider, useSessionCall } from 'react-sipjs';
import {
    Box,
    Modal,
    Paper,
    Avatar,
    Typography,
    IconButton,
    Stack,
    Chip,
} from '@mui/material';
import {
    Call as CallIcon,
    CallEnd as CallEndIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Pause as PauseIcon,
    PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { SessionState } from 'sip.js';

// Session Handler Component - uses useSessionCall hook to get session details
interface SessionHandlerProps {
    sessionId: string;
    onIncomingCall: (sessionId: string, callerInfo: { name?: string; number?: string }) => void;
    onCallEstablished: (sessionId: string) => void;
    onCallEnded: (sessionId: string) => void;
}

const SessionHandler: React.FC<SessionHandlerProps> = ({
    sessionId,
    onIncomingCall,
    onCallEstablished,
    onCallEnded,
}) => {
    const sessionData = useSessionCall(sessionId);
    const prevStateRef = useRef<string | null>(null);

    useEffect(() => {
        if (!sessionData) return;

        const { session, direction } = sessionData;
        const currentState = session?.state;

        // Only trigger callbacks when state changes
        if (currentState === prevStateRef.current) return;
        prevStateRef.current = currentState || null;

        console.log('=== Session Handler Update ===');
        console.log('Session ID:', sessionId);
        console.log('Direction:', direction);
        console.log('State:', currentState);

        // Detect incoming call
        if (direction === 'INCOMING' && currentState === SessionState.Initial) {
            const callerNumber = session?.remoteIdentity?.uri?.user || 'Unknown';
            const callerName = session?.remoteIdentity?.displayName || '';
            console.log('📞 Incoming call from:', callerNumber, callerName);
            onIncomingCall(sessionId, { name: callerName, number: callerNumber });
        }

        // Detect established call
        if (currentState === SessionState.Established) {
            console.log('✓ Call established');
            onCallEstablished(sessionId);
        }

        // Detect terminated call
        if (currentState === SessionState.Terminated) {
            console.log('✓ Call ended');
            onCallEnded(sessionId);
        }
    }, [sessionData, sessionId, onIncomingCall, onCallEstablished, onCallEnded]);

    return null;
};

// Active Call UI Component
interface ActiveCallUIProps {
    sessionId: string;
    onHangup: () => void;
}

const ActiveCallUI: React.FC<ActiveCallUIProps> = ({ sessionId, onHangup }) => {
    const sessionData = useSessionCall(sessionId);
    const [callDuration, setCallDuration] = useState(0);
    const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Call duration timer
    useEffect(() => {
        if (sessionData?.session?.state === SessionState.Established) {
            const startTime = sessionData.timer.answeredAt || sessionData.timer.createdAt;
            durationIntervalRef.current = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
                setCallDuration(elapsed);
            }, 1000);
        }

        return () => {
            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
            }
        };
    }, [sessionData?.session?.state, sessionData?.timer]);

    if (!sessionData) return null;

    const { session, isHeld, isMuted, hold, unhold, mute, unmute, hangup, direction } = sessionData;

    const formatDuration = (seconds: number): string => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hrs > 0) {
            return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleHangup = () => {
        hangup?.();
        onHangup();
    };

    const callerNumber = session?.remoteIdentity?.uri?.user || 'Unknown';
    const callerName = session?.remoteIdentity?.displayName || '';

    return (
        <Modal
            open={session?.state === SessionState.Established}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper
                elevation={5}
                sx={{
                    p: 4,
                    borderRadius: 3,
                    textAlign: 'center',
                    maxWidth: 350,
                    width: '90%',
                }}
            >
                <Avatar
                    sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: '#4CAF50',
                    }}
                >
                    <CallIcon sx={{ fontSize: 40 }} />
                </Avatar>

                <Chip
                    label={direction === 'INCOMING' ? 'Incoming' : 'Outgoing'}
                    color={direction === 'INCOMING' ? 'warning' : 'primary'}
                    size="small"
                    sx={{ mb: 1 }}
                />

                <Typography variant="h6" gutterBottom>
                    {callerName || callerNumber || 'On Call'}
                </Typography>

                <Typography variant="h4" color="primary" gutterBottom>
                    {formatDuration(callDuration)}
                </Typography>

                <Stack direction="row" spacing={1} justifyContent="center" mb={2}>
                    {isMuted && <Chip label="Muted" size="small" color="error" />}
                    {isHeld && <Chip label="On Hold" size="small" color="warning" />}
                </Stack>

                <Stack direction="row" spacing={2} justifyContent="center">
                    <IconButton
                        onClick={isMuted ? unmute : mute}
                        sx={{
                            backgroundColor: isMuted ? '#F44336' : '#42a5f5',
                            color: 'white',
                            width: 50,
                            height: 50,
                            '&:hover': {
                                backgroundColor: isMuted ? '#da190b' : '#1976d2',
                            },
                        }}
                    >
                        {isMuted ? <MicOffIcon /> : <MicIcon />}
                    </IconButton>

                    <IconButton
                        onClick={isHeld ? unhold : hold}
                        sx={{
                            backgroundColor: isHeld ? '#FF9800' : '#42a5f5',
                            color: 'white',
                            width: 50,
                            height: 50,
                            '&:hover': {
                                backgroundColor: isHeld ? '#f57c00' : '#1976d2',
                            },
                        }}
                    >
                        {isHeld ? <PlayArrowIcon /> : <PauseIcon />}
                    </IconButton>

                    <IconButton
                        onClick={handleHangup}
                        sx={{
                            backgroundColor: '#F44336',
                            color: 'white',
                            width: 50,
                            height: 50,
                            '&:hover': {
                                backgroundColor: '#da190b',
                            },
                        }}
                    >
                        <CallEndIcon />
                    </IconButton>
                </Stack>
            </Paper>
        </Modal>
    );
};

// Incoming Call UI Component
interface IncomingCallUIProps {
    sessionId: string;
    callerInfo: { name?: string; number?: string };
    onAnswer: () => void;
    onDecline: () => void;
}

const IncomingCallUI: React.FC<IncomingCallUIProps> = ({ sessionId, callerInfo, onAnswer, onDecline }) => {
    const sessionData = useSessionCall(sessionId);

    if (!sessionData) return null;

    const { session, answer, decline } = sessionData;

    // Only show for initial state incoming calls
    if (session?.state !== SessionState.Initial) return null;

    const handleAnswer = () => {
        answer?.();
        onAnswer();
    };

    const handleDecline = () => {
        decline?.();
        onDecline();
    };

    return (
        <Modal
            open={true}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper
                elevation={5}
                sx={{
                    p: 4,
                    borderRadius: 3,
                    textAlign: 'center',
                    maxWidth: 350,
                    width: '90%',
                }}
            >
                <Avatar
                    sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: '#FF9800',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        '@keyframes pulse': {
                            '0%': { transform: 'scale(1)', opacity: 1 },
                            '50%': { transform: 'scale(1.1)', opacity: 0.8 },
                            '100%': { transform: 'scale(1)', opacity: 1 },
                        },
                    }}
                >
                    <CallIcon sx={{ fontSize: 40 }} />
                </Avatar>

                <Typography variant="h6" gutterBottom>
                    Incoming Call
                </Typography>

                <Typography variant="body1" color="text.secondary" gutterBottom>
                    {callerInfo.name || callerInfo.number || 'Unknown Caller'}
                </Typography>

                <Chip
                    label="VICIDial"
                    color="primary"
                    size="small"
                    sx={{ mb: 3 }}
                />

                <Stack direction="row" spacing={4} justifyContent="center">
                    <IconButton
                        onClick={handleAnswer}
                        sx={{
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            width: 60,
                            height: 60,
                            '&:hover': {
                                backgroundColor: '#45a049',
                            },
                        }}
                    >
                        <CallIcon />
                    </IconButton>
                    <IconButton
                        onClick={handleDecline}
                        sx={{
                            backgroundColor: '#F44336',
                            color: 'white',
                            width: 60,
                            height: 60,
                            '&:hover': {
                                backgroundColor: '#da190b',
                            },
                        }}
                    >
                        <CallEndIcon />
                    </IconButton>
                </Stack>
            </Paper>
        </Modal>
    );
};

// SIP Connection Manager - handles registration and session monitoring
interface SIPConnectionManagerProps {
    user: any;
}

const SIPConnectionManager: React.FC<SIPConnectionManagerProps> = ({ user }) => {
    const { sessionManager, connectAndRegister, connectStatus, registerStatus, sessions } = useSIPProvider();
    const [isRegistered, setIsRegistered] = useState(false);
    const [incomingCallSession, setIncomingCallSession] = useState<{
        sessionId: string;
        callerInfo: { name?: string; number?: string };
    } | null>(null);
    const [activeCallSession, setActiveCallSession] = useState<string | null>(null);

    // Get credentials from user or query params
    const getCredentials = useCallback(() => {
        const params = new URLSearchParams(window.location.search);
        const usernameFromQuery = params.get('username');
        const passwordFromQuery = params.get('password');

        const username = usernameFromQuery || user?.mobile || '';
        const password = passwordFromQuery || 'iConnect786';

        return { username, password };
    }, [user]);

    // Auto-connect and register when user is available
    useEffect(() => {
        const { username, password } = getCredentials();

        if (username && password && !isRegistered && connectStatus !== 'CONNECTED') {
            console.log('=== Global SIP Registration ===');
            console.log('Username:', username);
            console.log('Connect Status:', connectStatus);

            connectAndRegister({
                username,
                password,
            });
        }
    }, [user, isRegistered, connectStatus, connectAndRegister, getCredentials]);

    // Track registration status
    useEffect(() => {
        if (registerStatus === 'REGISTERED') {
            setIsRegistered(true);
            console.log('✓ Global SIP: Successfully registered');
        }
    }, [registerStatus]);

    // Session event handlers
    const handleIncomingCall = useCallback((sessionId: string, callerInfo: { name?: string; number?: string }) => {
        console.log('📞 Global SIP: Setting incoming call state', sessionId);
        setIncomingCallSession({ sessionId, callerInfo });
    }, []);

    const handleCallEstablished = useCallback((sessionId: string) => {
        console.log('✓ Global SIP: Call established', sessionId);
        setIncomingCallSession(null);
        setActiveCallSession(sessionId);
    }, []);

    const handleCallEnded = useCallback((sessionId: string) => {
        console.log('✓ Global SIP: Call ended', sessionId);
        if (activeCallSession === sessionId) {
            setActiveCallSession(null);
        }
        if (incomingCallSession?.sessionId === sessionId) {
            setIncomingCallSession(null);
        }
    }, [activeCallSession, incomingCallSession]);

    const handleAnswerCall = useCallback(() => {
        console.log('✓ Global SIP: Call answered');
        // State will be updated by SessionHandler
    }, []);

    const handleDeclineCall = useCallback(() => {
        console.log('✓ Global SIP: Call declined');
        setIncomingCallSession(null);
    }, []);

    const handleHangup = useCallback(() => {
        console.log('✓ Global SIP: Hangup');
        setActiveCallSession(null);
    }, []);

    // Get session IDs
    const sessionIds = Object.keys(sessions);

    return (
        <>
            {/* Session Handlers - monitor each session */}
            {sessionIds.map((sessionId) => (
                <SessionHandler
                    key={sessionId}
                    sessionId={sessionId}
                    onIncomingCall={handleIncomingCall}
                    onCallEstablished={handleCallEstablished}
                    onCallEnded={handleCallEnded}
                />
            ))}

            {/* Incoming Call UI */}
            {incomingCallSession && (
                <IncomingCallUI
                    sessionId={incomingCallSession.sessionId}
                    callerInfo={incomingCallSession.callerInfo}
                    onAnswer={handleAnswerCall}
                    onDecline={handleDeclineCall}
                />
            )}

            {/* Active Call UI */}
            {activeCallSession && (
                <ActiveCallUI
                    sessionId={activeCallSession}
                    onHangup={handleHangup}
                />
            )}
        </>
    );
};

// Global SIP Provider Component
interface GlobalSIPProviderProps {
    user: any;
    loggedIn: boolean;
}

const GlobalSIPProviderComponent: React.FC<GlobalSIPProviderProps> = ({ user, loggedIn }) => {
    const [sipConfig, setSipConfig] = useState<any>(null);

    useEffect(() => {
        // Only initialize SIP when user is logged in
        if (!loggedIn || !user) {
            console.log('Global SIP: User not logged in, skipping SIP initialization');
            return;
        }

        console.log('=== Global SIP Provider Initialization ===');
        console.log('User:', user?.name, user?.mobile);
        console.log('User Type:', user?.type);

        const config = {
            domain: process.env.REACT_APP_VICIDIAL_DOMAIN || 'your-vicidial-server.com',
            webSocketServer: process.env.REACT_APP_VICIDIAL_WS_SERVER || 'wss://your-vicidial-server.com:8089/ws',
        };

        console.log('SIP Config:', config);
        setSipConfig(config);
    }, [user, loggedIn]);

    // Don't render SIP provider if user is not logged in or config not ready
    if (!loggedIn || !user || !sipConfig) {
        return null;
    }

    return (
        <SIPProvider options={sipConfig}>
            <SIPConnectionManager user={user} />
        </SIPProvider>
    );
};

// Connect to Redux
const mapStateToProps = (state: any) => ({
    user: state.user.user,
    loggedIn: state.user.loggedIn,
});

export default connect(mapStateToProps)(GlobalSIPProviderComponent);
